/**
 * How long a job has been running, and roughly how much is left.
 *
 * ---------------------------------------------------------------------------
 * THE ESTIMATE IS A BAND, SO THE TIMER IS A BAND
 * ---------------------------------------------------------------------------
 * A booking carries `duration` as human copy - "2 hrs", "2–3 hrs", "45 mins" -
 * because that is what was quoted at checkout and what the professional was
 * briefed with. It is not a deadline, and nothing on the platform promises the
 * job ends at a particular second.
 *
 * The panel counts DOWN, because "how much longer" is the question somebody
 * standing in their hallway is actually asking. But the number it counts down
 * to is the TOP of the estimate, and what happens at zero is the whole care
 * of this file: the clock stops, it does not go negative, and the copy admits
 * the estimate was an estimate.
 *
 * That matters because a countdown implies a promise. A ticker reading
 * "0:47:13" claims a precision the quote never had, and if it hit zero and
 * kept going - or turned red - it would have told the customer they were
 * wronged by a job that is merely taking as long as jobs sometimes take. A
 * support ticket manufactured out of a rounding choice.
 *
 * So three kinds of number, and the panel picks between them:
 *   countdown  toward the top of the estimate, exact, ticks every second,
 *              and null once spent - the big line while it lasts
 *   elapsed    counted up from `startedAt`, exact, and what the big line
 *              falls back to once the countdown has nothing left to say
 *   remaining  the same span in words, quoted as a RANGE where the estimate
 *              was a range, because "2–3 hrs" genuinely has two ends
 *
 * ---------------------------------------------------------------------------
 * OVERRUN IS A STATE, NOT AN ERROR
 * ---------------------------------------------------------------------------
 * Jobs overrun. A sofa has more stains than the photo showed. When the
 * countdown runs out the panel switches back to counting UP and says plainly
 * that it is taking longer than estimated - rather than freezing at "0:00",
 * going negative, or turning red. The work is still happening, the customer
 * is not owed an alarm, and "1:14 over the 2 hrs estimate" is exactly what a
 * person in the room can already see.
 */

/** The estimate, parsed out of the copy it was quoted in. */
export type DurationBand = {
  /** Low end, in minutes. Equal to `maxMinutes` for a flat "2 hrs". */
  minMinutes: number;
  maxMinutes: number;
};

/**
 * Read "2 hrs", "2–3 hrs", "45 mins" into minutes.
 *
 * Both dash characters work: the seed data uses an en dash and anything typed
 * by hand or returned by an API is likely to use a hyphen, and the number
 * scan below does not care which. Returns null when there is no number to
 * find at all, which is the signal to draw no estimate rather than invent one.
 */
export const parseDuration = (duration?: string): DurationBand | null => {
  if (!duration) {
    return null;
  }

  const hours = /hr|hour/i.test(duration);
  const numbers = duration.match(/\d+(?:\.\d+)?/g);
  if (!numbers || numbers.length === 0) {
    return null;
  }

  const toMinutes = (value: string) => {
    const amount = Number(value);
    return Math.round(hours ? amount * 60 : amount);
  };

  const low = toMinutes(numbers[0]);
  const high = numbers.length > 1 ? toMinutes(numbers[1]) : low;

  if (!Number.isFinite(low) || low <= 0) {
    return null;
  }
  return { minMinutes: low, maxMinutes: Math.max(low, high) };
};

/**
 * Turn "10:18 AM" into a real instant on the day the job started.
 *
 * The seed rows carry a wall-clock string rather than an ISO stamp, so the
 * date has to come from somewhere: `now` supplies it. A job that started at
 * 11:50 PM and is still running after midnight would otherwise measure as
 * nearly a full day of elapsed time, so a start that lands in the future is
 * rolled back a day - the only reading that can be true of a job already
 * under way.
 *
 * Returns null on anything unparseable, and the panel then shows the estimate
 * it always showed rather than a wrong timer.
 */
export const startedAtInstant = (
  startedAt: string | undefined,
  now: Date,
): Date | null => {
  if (!startedAt) {
    return null;
  }

  /* ISO first - what a real API will send, and it needs no guessing. */
  if (/\d{4}-\d{2}-\d{2}/.test(startedAt)) {
    const parsed = new Date(startedAt);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const match = startedAt.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])?/);
  if (!match) {
    return null;
  }

  let hour = Number(match[1]);
  const minute = Number(match[2]);
  const meridiem = match[3] ? match[3].toUpperCase() : undefined;

  if (hour > 23 || minute > 59) {
    return null;
  }

  if (meridiem === 'PM' && hour < 12) {
    hour += 12;
  }
  if (meridiem === 'AM' && hour === 12) {
    hour = 0;
  }

  const start = new Date(now);
  start.setHours(hour, minute, 0, 0);

  /* Started "later today" than it is now means it started yesterday. */
  if (start.getTime() > now.getTime()) {
    start.setDate(start.getDate() - 1);
  }
  return start;
};

/** `h:mm:ss`, or `m:ss` under an hour - the shape a running clock is read in. */
export const elapsedClock = (seconds: number): string => {
  const safe = Math.max(0, Math.floor(seconds));
  const hrs = Math.floor(safe / 3600);
  const mins = Math.floor((safe % 3600) / 60);
  const secs = safe % 60;

  const pad = (value: number) => String(value).padStart(2, '0');

  if (hrs > 0) {
    return `${hrs}:${pad(mins)}:${pad(secs)}`;
  }
  return `${mins}:${pad(secs)}`;
};

/** Minutes as words - "40 mins", "1 hr 20 mins". */
export const humanMinutes = (minutes: number): string => {
  const total = Math.max(0, Math.round(minutes));
  const hrs = Math.floor(total / 60);
  const mins = total % 60;

  if (hrs === 0) {
    return `${mins} ${mins === 1 ? 'min' : 'mins'}`;
  }
  if (mins === 0) {
    return `${hrs} ${hrs === 1 ? 'hr' : 'hrs'}`;
  }
  return `${hrs} hr ${mins} mins`;
};

export type JobProgress = {
  /** Seconds since the job started. Always exact, always counting up. */
  elapsedSeconds: number;
  /** `elapsedSeconds` formatted for the big line. */
  elapsed: string;
  /**
   * What is left, in words, or null where there is no estimate to work from.
   *
   * A range while the band has room ("20 mins–50 mins left"), a single figure
   * once only the top of the band remains, and null once it is spent -
   * `overrun` carries that case instead.
   */
  remaining: string | null;
  /**
   * Seconds left against the TOP of the estimate, floored at zero.
   *
   * Zero the moment the estimate is spent, and it stays there - the clock
   * does not go negative and start counting the overrun downward, which would
   * read as a job getting further from finishing the longer it runs.
   */
  remainingSeconds: number;
  /**
   * `remainingSeconds` as a clock, for the big line - the figure that ticks
   * DOWN toward the end of the estimate.
   *
   * Null once the estimate is spent, and null when there was no estimate to
   * count against: both cases have no honest number to show, and the panel
   * falls back to the elapsed clock rather than displaying a frozen "0:00"
   * on work that is still going.
   */
  countdown: string | null;
  /** True once elapsed has passed the top of the estimate. */
  overrun: boolean;
  /**
   * How much of the estimate is USED UP, 0–1.
   *
   * Measured against the top of the band, so 1 lines up with the moment the
   * estimate is genuinely spent rather than the moment the job is over.
   * Clamped at 1.
   *
   * Note the direction before drawing with it: this counts up as time is
   * consumed, and the bar on `BookingDetailScreen` deliberately renders
   * `1 - fraction` so it drains rather than fills. The value is kept in the
   * "spent" direction because that is what the arithmetic above produces and
   * what "overrun at 1" reads naturally as.
   */
  fraction: number;
};

/**
 * The live state of a job in progress.
 *
 * Pure, and `now` is passed in rather than read here, so a test can pin it to
 * a fixed instant and the component can decide how often to re-run it.
 * Returns null when the job has no readable start - the caller then shows the
 * static estimate it always showed.
 */
export const jobProgress = (
  startedAt: string | undefined,
  duration: string | undefined,
  now: Date = new Date(),
): JobProgress | null => {
  const start = startedAtInstant(startedAt, now);
  if (!start) {
    return null;
  }

  const elapsedSeconds = Math.max(
    0,
    Math.floor((now.getTime() - start.getTime()) / 1000),
  );
  const band = parseDuration(duration);

  /* A start with no estimate still gives an honest running clock. */
  if (!band) {
    return {
      elapsedSeconds,
      elapsed: elapsedClock(elapsedSeconds),
      remaining: null,
      /* Nothing to count down to, so the panel shows the elapsed clock. */
      remainingSeconds: 0,
      countdown: null,
      overrun: false,
      fraction: 0,
    };
  }

  const elapsedMinutes = elapsedSeconds / 60;
  const toLow = band.minMinutes - elapsedMinutes;
  const toHigh = band.maxMinutes - elapsedMinutes;

  /*
   * The range collapses to one figure as it is used up. "10 mins–40 mins left"
   * is useful; "0 mins–40 mins left" is not, and neither is a range on a flat
   * estimate that never had two ends.
   */
  let remaining: string | null = null;
  if (toHigh > 0) {
    remaining =
      toLow > 0 && band.minMinutes !== band.maxMinutes
        ? `${humanMinutes(toLow)}–${humanMinutes(toHigh)} left`
        : `about ${humanMinutes(toHigh)} left`;
  }

  const remainingSeconds = Math.max(0, Math.round(toHigh * 60));

  return {
    elapsedSeconds,
    elapsed: elapsedClock(elapsedSeconds),
    remaining,
    remainingSeconds,
    /* Null rather than "0:00" once spent - see the field's own note. */
    countdown: remainingSeconds > 0 ? elapsedClock(remainingSeconds) : null,
    overrun: toHigh <= 0,
    fraction: Math.min(1, elapsedMinutes / band.maxMinutes),
  };
};
